import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormHelperText,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import {
  useGetNotificationTemplateQuery,
  useCreateNotificationTemplateMutation,
  useUpdateNotificationTemplateMutation,
} from '../../services/connectApi';
import type { NotificationTemplate } from '../../models';
import Grid from '@mui/material/GridLegacy';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`template-tabpanel-${index}`}
      aria-labelledby={`template-tab-${index}`}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const TemplateForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = !!id;

  const [activeTab, setActiveTab] = useState(0);
  const [template, setTemplate] = useState<Partial<NotificationTemplate>>({
    name: '',
    description: '',
    push_title: '',
    push_body: '',
    push_data: {},
    push_image_url: '',
    email_subject: '',
    email_body: '',
    email_html_body: '',
    sms_body: '',
    whatsapp_body: '',
    whatsapp_template_name: '',
    variables: [],
  });
  const [variableInput, setVariableInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: existingTemplate, isLoading } = useGetNotificationTemplateQuery(
    id as string,
    { skip: !isEditMode }
  );
  
  const [createTemplate] = useCreateNotificationTemplateMutation();
  const [updateTemplate] = useUpdateNotificationTemplateMutation();

  useEffect(() => {
    if (isEditMode && existingTemplate) {
      setTemplate(existingTemplate);
    } else if (!isEditMode) {
      // Check if we have template data from navigation state (for duplication)
      const stateTemplate = location.state?.template as Partial<NotificationTemplate>;
      if (stateTemplate) {
        setTemplate(prev => ({ ...prev, ...stateTemplate }));
      }
    }
  }, [isEditMode, existingTemplate, location.state]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleInputChange = (field: keyof NotificationTemplate, value: any) => {
    setTemplate((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field if it exists
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handlePushDataChange = (key: string, value: string) => {
    setTemplate((prev) => ({
      ...prev,
      push_data: {
        ...(prev.push_data || {}),
        [key]: value,
      },
    }));
  };

  const handleAddVariable = () => {
    if (!variableInput.trim()) return;
    
    const newVariable = variableInput.trim();
    if (!template.variables?.includes(newVariable)) {
      setTemplate((prev) => ({
        ...prev,
        variables: [...(prev.variables || []), newVariable],
      }));
    }
    setVariableInput('');
  };

  const handleRemoveVariable = (variable: string) => {
    setTemplate((prev) => ({
      ...prev,
      variables: (prev.variables || []).filter((v) => v !== variable),
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!template.name) {
      newErrors.name = 'Name is required';
    }
    
    // At least one channel content should be provided
    const hasContent = 
      (template.push_title && template.push_body) || 
      (template.email_subject && (template.email_body || template.email_html_body)) || 
      template.sms_body || 
      (template.whatsapp_body && template.whatsapp_template_name);
    
    if (!hasContent) {
      newErrors.content = 'Content for at least one notification channel is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    try {
      if (isEditMode) {
        await updateTemplate({
          id: id as string,
          data: template,
        }).unwrap();
      } else {
        await createTemplate(template).unwrap();
      }
      navigate('/connect#templates');
    } catch (error) {
      console.error('Failed to save template:', error);
      setErrors({
        submit: 'Failed to save template. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isEditMode && isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/connect#templates')}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h5" component="h1">
          {isEditMode ? 'Edit Notification Template' : 'Create Notification Template'}
        </Typography>
      </Box>

      {errors.submit && (
        <Box sx={{ bgcolor: 'error.light', color: 'error.contrastText', p: 2, borderRadius: 1, mb: 3 }}>
          <Typography variant="body1">{errors.submit}</Typography>
        </Box>
      )}

      <Paper elevation={3}>
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Template Name"
                fullWidth
                required
                value={template.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name || ''}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Description"
                fullWidth
                value={template.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="template content tabs">
            <Tab label="Push Notification" id="template-tab-0" aria-controls="template-tabpanel-0" />
            <Tab label="Email" id="template-tab-1" aria-controls="template-tabpanel-1" />
            <Tab label="SMS" id="template-tab-2" aria-controls="template-tabpanel-2" />
            <Tab label="WhatsApp" id="template-tab-3" aria-controls="template-tabpanel-3" />
            <Tab label="Variables" id="template-tab-4" aria-controls="template-tabpanel-4" />
          </Tabs>
        </Box>

        {errors.content && (
          <Box sx={{ p: 2, color: 'error.main' }}>
            <Typography variant="body2">{errors.content}</Typography>
          </Box>
        )}

        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Push Title"
                fullWidth
                value={template.push_title || ''}
                onChange={(e) => handleInputChange('push_title', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Push Body"
                fullWidth
                multiline
                rows={4}
                value={template.push_body || ''}
                onChange={(e) => handleInputChange('push_body', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Image URL"
                fullWidth
                value={template.push_image_url || ''}
                onChange={(e) => handleInputChange('push_image_url', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Push Data (Additional JSON Payload)
              </Typography>
              <Card variant="outlined">
                <CardContent>
                  <Grid container spacing={2}>
                    {Object.entries(template.push_data || {}).map(([key, value], index) => (
                      <React.Fragment key={index}>
                        <Grid item xs={5}>
                          <TextField
                            label="Key"
                            fullWidth
                            value={key}
                            onChange={(e) => {
                              const newData = { ...template.push_data };
                              const oldValue = newData[key];
                              delete newData[key];
                              newData[e.target.value] = oldValue;
                              handleInputChange('push_data', newData);
                            }}
                          />
                        </Grid>
                        <Grid item xs={5}>
                          <TextField
                            label="Value"
                            fullWidth
                            value={value}
                            onChange={(e) => handlePushDataChange(key, e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <Button
                            color="error"
                            onClick={() => {
                              const newData = { ...template.push_data };
                              delete newData[key];
                              handleInputChange('push_data', newData);
                            }}
                            sx={{ mt: 1 }}
                          >
                            Remove
                          </Button>
                        </Grid>
                      </React.Fragment>
                    ))}
                    <Grid item xs={12}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          const newKey = `key_${Object.keys(template.push_data || {}).length + 1}`;
                          handlePushDataChange(newKey, '');
                        }}
                      >
                        Add Data Field
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Email Subject"
                fullWidth
                value={template.email_subject || ''}
                onChange={(e) => handleInputChange('email_subject', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email Plain Text Body"
                fullWidth
                multiline
                rows={4}
                value={template.email_body || ''}
                onChange={(e) => handleInputChange('email_body', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email HTML Body"
                fullWidth
                multiline
                rows={8}
                value={template.email_html_body || ''}
                onChange={(e) => handleInputChange('email_html_body', e.target.value)}
              />
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="SMS Body"
                fullWidth
                multiline
                rows={4}
                value={template.sms_body || ''}
                onChange={(e) => handleInputChange('sms_body', e.target.value)}
              />
              <FormHelperText>
                Keep SMS messages concise and under 160 characters when possible
              </FormHelperText>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="WhatsApp Template Name"
                fullWidth
                value={template.whatsapp_template_name || ''}
                onChange={(e) => handleInputChange('whatsapp_template_name', e.target.value)}
              />
              <FormHelperText>
                The name of the pre-approved WhatsApp template
              </FormHelperText>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="WhatsApp Body"
                fullWidth
                multiline
                rows={4}
                value={template.whatsapp_body || ''}
                onChange={(e) => handleInputChange('whatsapp_body', e.target.value)}
              />
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Template Variables
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Define variables that can be used in the template using the format &#123; &#123; variable_name &#125; &#125; .
                These variables will be replaced with actual values when sending notifications.
              </Typography>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                label="Add Variable"
                fullWidth
                value={variableInput}
                onChange={(e) => setVariableInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddVariable();
                  }
                }}
                placeholder="e.g. user_name, team_name"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                onClick={handleAddVariable}
                fullWidth
                sx={{ height: '56px' }}
              >
                Add Variable
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mt: 2 }}>
                {template.variables?.length ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {template.variables.map((variable) => (
                      <Chip
                        key={variable}
                        label={variable}
                        onDelete={() => handleRemoveVariable(variable)}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No variables defined yet.
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </TabPanel>

        <Box sx={{ p: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Template'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default TemplateForm;
